import { useRequest } from 'features/request/useRequest';
import { useResetStoreData } from 'features/request/useResetStoreData';
import type { UerBaseCostChangeType } from 'gql/graphql';

import { useCallback, useEffect, useMemo, useState } from 'react';

import { catalogUERBaseCostHistoryWorkScopesGql } from '../catalogPriceHistoryQueries';

interface IUsePriceHistoryDataProps {
    catalogUniqueId: string;
    historyRecordId: number;
}

export type TFilterType = 'typeFilter' | 'nameFilter';
export type TFilter = {
    typeFilter: UerBaseCostChangeType | null;
    nameFilter: string[];
};

const requestId = 'usePriceHistoryData';

export const usePriceHistoryData = ({ catalogUniqueId, historyRecordId }: IUsePriceHistoryDataProps) => {
    const [filterValue, setFilterValue] = useState<TFilter | null>(null);

    const variables = useMemo(
        () => ({
            catalogUniqueId,
            historyRecordId,
        }),
        [catalogUniqueId, historyRecordId],
    );

    const handleFilterChange = useCallback(
        ({ filterType, value }: { filterType?: TFilterType; value?: TFilter[TFilterType] } = {}) => {
            setFilterValue((prev) => {
                if (filterType) {
                    return {
                        ...prev,
                        [filterType]: value,
                    } as TFilter;
                }

                return prev;
            });
        },
        [],
    );

    const [data] = useRequest({
        requestId,
        query: catalogUERBaseCostHistoryWorkScopesGql,
        variables,
    });

    const [reset] = useResetStoreData({
        requestId,
    });

    useEffect(
        () => () => {
            reset(undefined);
        },
        [reset],
    );

    const countsObj = useMemo(() => {
        const {
            deletedScopesCount = 0,
            newScopesCount = 0,
            updatedScopesCount = 0,
        } = data?.catalogUERBaseCostHistoryWorkScopes?.data?.counts || {};

        return {
            deletedCostsCount: deletedScopesCount,
            updatedCostsCount: updatedScopesCount,
            createdCostsCount: newScopesCount,
        };
    }, [data?.catalogUERBaseCostHistoryWorkScopes?.data?.counts]);

    const nameFilterData = useMemo(
        () =>
            data?.catalogUERBaseCostHistoryWorkScopes?.data?.workScopeItems?.map((item) => ({
                name: item?.name,
                label: item?.name,
            })),
        [data?.catalogUERBaseCostHistoryWorkScopes?.data?.workScopeItems],
    );

    const tableRows = useMemo(
        () =>
            data?.catalogUERBaseCostHistoryWorkScopes?.data?.workScopeItems
                ?.filter((item) => (filterValue?.typeFilter ? item?.changeType === filterValue?.typeFilter : true))
                .filter((item) =>
                    filterValue?.nameFilter?.length ? filterValue?.nameFilter?.includes(item?.name) : true,
                ),
        [
            data?.catalogUERBaseCostHistoryWorkScopes?.data?.workScopeItems,
            filterValue?.nameFilter,
            filterValue?.typeFilter,
        ],
    );

    return {
        countsObj,
        tableRows,
        nameFilterData,
        handleFilterChange,
    };
};
